/**
 * Direct Supabase Quotes Hook
 * Bypasses Railway API for faster data loading
 * Uses Supabase RLS for security (same as backend)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

// Transformed quote interface (after extraction from JSONB)
interface Quote {
  id: string;
  companyId: string;
  userId: string;
  number: string;
  customerName: string;
  customerContact: Record<string, unknown>;
  input: Record<string, unknown>;
  breakdown: Record<string, unknown>;
  vendorProfit: number;
  unitCostWithProfit: number;
  totalWithProfit: number;
  totalWithMargin: number;
  pricingVersion: string;
  currency: string;
  status: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  expiryDate: string;
  archivedAt: string | null;
}

interface DashboardStats {
  myQuotes: number;
  myQuotesValue: number;
  thisMonthQuotes: number;
  thisMonthValue: number;
  thisWeekQuotes: number;
  expiringQuotes: number;
  recentQuotes: Quote[];
}

// Transform quote from Supabase to frontend format
// NOTE: Quote data is stored in JSONB columns (specifications, calculations)
// not as direct columns. We need to extract from these nested objects.
function transformQuote(quote: Record<string, unknown>): Record<string, unknown> {
  // Extract data from JSONB columns
  const specs = (quote.specifications as Record<string, unknown>) || {};
  const calcs = (quote.calculations as Record<string, unknown>) || {};
  const metadata = (quote.metadata as Record<string, unknown>) || {};
  
  return {
    id: quote.id,
    companyId: quote.company_id,
    userId: quote.user_id,
    number: quote.quote_number || quote.number,
    // From specifications JSONB
    customerName: specs.customerName,
    customerContact: specs.customerContact || {},
    input: specs.input || {},
    notes: specs.notes || '',
    currency: specs.currency || 'PKR',
    taxPercent: specs.taxPercent || 0,
    // From calculations JSONB
    breakdown: calcs.breakdown || {},
    vendorProfit: calcs.vendorProfit || 0,
    unitCostWithProfit: calcs.unitCostWithProfit,
    totalWithProfit: calcs.totalWithProfit,
    totalWithMargin: calcs.totalWithProfit, // alias for backward compat
    totalWithTax: calcs.totalWithTax,
    finalTotalWithAgentMargin: calcs.finalTotalWithAgentMargin,
    pricingVersion: calcs.pricingVersion,
    variants: calcs.variants,
    primaryQuantity: calcs.primaryQuantity,
    // Direct columns
    status: quote.status,
    createdAt: quote.created_at,
    updatedAt: quote.updated_at,
    expiryDate: metadata.expiryDate || quote.expires_at,
    archivedAt: quote.archived_at,
  };
}

/**
 * Direct Supabase query for quotes - bypasses Railway cold start
 */
export function useDirectQuotes() {
  const { user } = useAuth();
  const companyId = user?.company?.id || user?.companyId || user?.orgId;
  const userId = user?.id;
  const role = user?.role;

  return useQuery({
    queryKey: ['direct-quotes', companyId, userId, role],
    queryFn: async () => {
      if (!companyId) {
        return { quotes: [], stats: getEmptyStats() };
      }

      let query = supabase
        .from('quotes')
        .select('*')
        .eq('company_id', companyId)
        .is('archived_at', null)
        .order('created_at', { ascending: false });

      // Role-based filtering (agents only see their own quotes)
      if (role === 'agent') {
        query = query.eq('user_id', userId);
      }

      const { data: quotes, error } = await query;

      if (error) {
        console.error('[DirectSupabase] Error fetching quotes:', error);
        throw error;
      }

      const transformedQuotes = (quotes || []).map(transformQuote);
      const stats = calculateStats(transformedQuotes);

      // Debug logging for dashboard stats
      if (import.meta.env.DEV || localStorage.getItem('debug_dashboard')) {
        console.log('[DirectSupabase] Quotes fetched:', quotes?.length);
        console.log('[DirectSupabase] First quote raw calculations:', quotes?.[0]?.calculations);
        console.log('[DirectSupabase] First quote transformed totalWithProfit:', transformedQuotes?.[0]?.totalWithProfit);
        console.log('[DirectSupabase] Stats calculated:', stats);
      }

      return {
        quotes: transformedQuotes,
        stats,
      };
    },
    enabled: !!companyId && !!userId,
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

/**
 * Get dashboard stats directly from Supabase
 */
export function useDirectDashboardStats() {
  const { data, isLoading, error, refetch } = useDirectQuotes();

  return {
    stats: data?.stats || getEmptyStats(),
    quotes: data?.quotes || [],
    isLoading,
    error,
    refetch,
  };
}

function getEmptyStats(): DashboardStats {
  return {
    myQuotes: 0,
    myQuotesValue: 0,
    thisMonthQuotes: 0,
    thisMonthValue: 0,
    thisWeekQuotes: 0,
    expiringQuotes: 0,
    recentQuotes: [],
  };
}

function calculateStats(quotes: Record<string, unknown>[]): DashboardStats {
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const thisMonthQuotes = quotes.filter(
    (q) => new Date(q.createdAt as string) >= thisMonth
  );
  const thisWeekQuotes = quotes.filter(
    (q) => new Date(q.createdAt as string) >= thisWeek
  );
  const expiringQuotes = quotes.filter((q) => {
    const expiry = new Date(q.expiryDate as string);
    return expiry <= nextWeek && expiry > now;
  });

  // Helper to extract total value from a quote (checks multiple possible fields)
  const getQuoteTotal = (q: Record<string, unknown>): number => {
    const breakdown = q.breakdown as Record<string, unknown> | undefined;
    return (
      (q.totalWithProfit as number) ||
      (q.totalWithMargin as number) ||
      (q.totalWithTax as number) ||
      (q.finalTotalWithAgentMargin as number) ||
      (breakdown?.total as number) ||
      (breakdown?.totalWithMargin as number) ||
      (breakdown?.totalWithProfit as number) ||
      0
    );
  };

  const totalValue = quotes.reduce((sum, q) => sum + getQuoteTotal(q), 0);

  const thisMonthValue = thisMonthQuotes.reduce((sum, q) => sum + getQuoteTotal(q), 0);

  return {
    myQuotes: quotes.length,
    myQuotesValue: totalValue,
    thisMonthQuotes: thisMonthQuotes.length,
    thisMonthValue,
    thisWeekQuotes: thisWeekQuotes.length,
    expiringQuotes: expiringQuotes.length,
    recentQuotes: quotes.slice(0, 5) as Quote[],
  };
}
